import { lazy, useEffect } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Outlet,
  useParams,
} from "react-router";
import { Route } from "react-router";
import { PATH } from "../Constants/paths";
import { ScrollOnNavigate } from "./ScrollOnNavigate";
import { PrivateRoute } from "./privateRoute";
import { USER_ROLE } from "../Types/user";
import { MainLayout } from "../Pages/Layouts/MainLayout";
import { suppressPreloadErrors } from "../utils/chunkReload";
import { CAPABILITY_KEYS } from "../utils/capabilities";
const JobResultPage = lazy(() => import("../Pages/JobResultPage"));

// ---> Auth Pages
const LoginPage = lazy(() => import("../Pages/Auth/LoginPage"));
const TeamsOauthCallbackPage = lazy(
  () => import("../Pages/Settings/TeamsOauthCallbackPage"),
);
const TeamsAdminApprovalPage = lazy(
  () => import("../Pages/Settings/TeamsAdminApprovalPage"),
);
const MessagingOauthCallbackPage = lazy(
  () => import("../Pages/Settings/MessagingOauthCallbackPage"),
);
const AuthCallbackPage = lazy(() => import("../Pages/Auth/AuthCallbackPage"));
const McpConnectionsPage = lazy(() => import("../features/mcp/McpConnectionsPage"));
const CrmOauthCallbackPage = lazy(
  () => import("../Pages/Settings/CrmOauthCallbackPage"),
);
const ActivateAccountPage = lazy(
  () => import("../Pages/Auth/ActivateAccountPage"),
);
const ForgotPasswordPage = lazy(
  () => import("../Pages/Auth/ForgotPasswordPage"),
);
const ResetPasswordPage = lazy(() => import("../Pages/Auth/ResetPasswordPage"));
const SignupPage = lazy(() => import("../Pages/Auth/SignupPage"));
const SignupSuccessPage = lazy(() => import("../Pages/Auth/SignupSuccessPage"));
const SignupCancelPage = lazy(() => import("../Pages/Auth/SignupCancelPage"));
const SubscriptionInactivePage = lazy(
  () => import("../Pages/Auth/SubscriptionInactivePage"),
);
import { BillingReactivationProvider } from "../context/BillingReactivationContext";

// ---> Error Pages
const AccessDeniedPage = lazy(() => import("../Pages/Error/AccessDenied"));
const NotFoundPage = lazy(() => import("../Pages/Error/NotFound"));

// ---> Static Pages
const LandingPage = lazy(() => import("../Pages/Static/LandingPage"));
const PricingPage = lazy(() => import("../Pages/Static/PricingPage"));
const SecurityPage = lazy(() => import("../Pages/Static/SecurityPage"));
const HelpPage = lazy(() => import("../Pages/Static/HelpPage"));
const ContactPage = lazy(() => import("../Pages/Static/ContactPage"));
const DemoPage = lazy(() => import("../Pages/Static/DemoPage"));
const SlackIntegrationPage = lazy(
  () => import("../Pages/Static/SlackIntegrationPage"),
);
const SlackSupportPage = lazy(() => import("../Pages/Static/SlackSupportPage"));
const TeamsIntegrationPage = lazy(
  () => import("../Pages/Static/TeamsIntegrationPage"),
);
const TeamsSupportPage = lazy(() => import("../Pages/Static/TeamsSupportPage"));
const IntegrationsPage = lazy(() => import("../Pages/Static/IntegrationsPage"));
const CrmIntegrationPage = lazy(
  () => import("../Pages/Static/CrmIntegrationPage"),
);

// ---> Account Pages
const UpdateAccountPage = lazy(
  () => import("../Pages/Account/AllAccountsPage"),
);
const AccountDetailsPage = lazy(
  () => import("../Pages/Account/AccountDetailsPage"),
);

// ---> Industry Pages
const IndustryInsightsPage = lazy(
  () => import("../Pages/Industry/IndustryInsightsPage"),
);
const IntelligencePreviewPage = import.meta.env.DEV
  ? lazy(() => import("../features/insights/Preview"))
  : null;
const DesignSystemPreviewPage = import.meta.env.DEV
  ? lazy(() => import("../dev/DesignSystemPreview"))
  : null;

// ---> Legal Pages
const LegalTemplatePage = lazy(
  () => import("../Pages/Legal/LegalTemplatePage"),
);

// ---> Settings Pages
const SettingsPage = lazy(() => import("../Pages/Settings/SettingsPage"));
const DismissalDecisionPage = lazy(
  () => import("../Pages/Account/DismissalDecisionPage"),
);
const CrmMatchReviewsPage = lazy(
  () => import("../Pages/Crm/CrmMatchReviewsPage"),
);

// ---> Admin Pages
const CreateClientPage = lazy(() => import("../Pages/Admin/CreateClientPage"));
const CreateCampaignPage = lazy(
  () => import("../Pages/Admin/CreateCampaignPage"),
);
const ClientReadinessPage = lazy(
  () => import("../Pages/Admin/ClientReadinessPage"),
);
const ClientLimitsPage = lazy(() => import("../Pages/Admin/ClientLimitsPage"));
const CostsPage = lazy(() => import("../Pages/Admin/CostsPage"));

// --> Sales Home Pages
const SalesHomePage = lazy(() => import("../Pages/SalesHome/SalesHomePage"));

// --> Assets Pages
const MyAssetsPage = lazy(() => import("../Pages/Assets/MyAssetsPage"));

// --> Activity Pages
const ActivityPage = lazy(() => import("../Pages/Activity/ActivityPage"));
const AskIntenticoPage = lazy(
  () => import("../Pages/Assistant/AskIntenticoPage"),
);

const ALL_ROLES = [
  USER_ROLE.ADMIN,
  USER_ROLE.SALES_LEADER,
  USER_ROLE.USER,
  USER_ROLE.SALES_REP,
  USER_ROLE.CLIENT_ADMIN,
];

const ADMIN_ONLY_ROLES = [USER_ROLE.ADMIN];
const CAMPAIGN_ADMIN_ROLES = [USER_ROLE.ADMIN, USER_ROLE.CLIENT_ADMIN];

function preloadTabChunks() {
  const idle =
    window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 200));
  idle(() => {
    void suppressPreloadErrors(() =>
      Promise.allSettled([
        import("../Pages/SalesHome/SalesHomePage"),
        import("../Pages/Account/AllAccountsPage"),
        import("../Pages/Industry/IndustryInsightsPage"),
        import("../Pages/Assets/MyAssetsPage"),
        import("../Pages/Activity/ActivityPage"),
        import("../Pages/Account/AccountDetailsPage"),
        import("../Pages/Settings/SettingsPage"),
        import("../Pages/Crm/CrmMatchReviewsPage"),
      ]),
    );
  });
}

export const PageRoutes = () => {
  useEffect(() => {
    preloadTabChunks();
  }, []);
  return <RouterProvider router={appRouter} />;
};

const AccountRouteWrapper = () => {
  const { id } = useParams();
  return <AccountDetailsPage id={id} />;
};

// Keep the existing route tree and providers; data routing enables safe draft navigation blocking.
export const appRoutes = createRoutesFromElements(
  <Route
    element={
      <BillingReactivationProvider>
        <ScrollOnNavigate />
        <Outlet />
      </BillingReactivationProvider>
    }
  >
    {IntelligencePreviewPage && (
      <Route path="/dev/insights/*" Component={IntelligencePreviewPage} />
    )}
    {DesignSystemPreviewPage && (
      <Route path="/dev/design-system" Component={DesignSystemPreviewPage} />
    )}
    {/* Auth Page Routes */}
    <Route path={PATH.LOGIN} Component={LoginPage} />
    <Route path={PATH.AUTH_CALLBACK} Component={AuthCallbackPage} />
    <Route path="/mcp/authorize" Component={McpConnectionsPage} />
    <Route path="/settings/connected-applications" Component={McpConnectionsPage} />
    <Route path={PATH.CRM_OAUTH_CALLBACK} Component={CrmOauthCallbackPage} />
    <Route
      path="/settings/messaging/oauth/callback"
      Component={MessagingOauthCallbackPage}
    />
    <Route
      path="/settings/messaging/teams/callback"
      Component={TeamsOauthCallbackPage}
    />
    <Route
      path="/integrations/microsoft-teams/approve"
      Component={TeamsAdminApprovalPage}
    />
    <Route path={PATH.ACTIVATE_ACCOUNT} Component={ActivateAccountPage} />
    <Route path={PATH.FORGOT_PASSWORD} Component={ForgotPasswordPage} />
    <Route path={PATH.RESET_PASSWORD} Component={ResetPasswordPage} />
    <Route path={PATH.SIGNUP} Component={SignupPage} />
    <Route path={PATH.SIGNUP_SUCCESS} Component={SignupSuccessPage} />
    <Route path={PATH.SIGNUP_CANCEL} Component={SignupCancelPage} />
    <Route
      path={PATH.SUBSCRIPTION_INACTIVE}
      Component={SubscriptionInactivePage}
    />

    {/* Static Pages Routes */}
    <Route path={PATH.LANDING} Component={LandingPage} />
    <Route path={PATH.PRICING} Component={PricingPage} />
    <Route path={PATH.SECURITY} Component={SecurityPage} />
    <Route path={PATH.HELP} Component={HelpPage} />
    <Route path={PATH.CONTACT} Component={ContactPage} />
    <Route path={PATH.DEMO} Component={DemoPage} />
    <Route path={PATH.SLACK_INTEGRATION} Component={SlackIntegrationPage} />
    <Route path={PATH.TEAMS_INTEGRATION} Component={TeamsIntegrationPage} />
    <Route path={PATH.TEAMS_SUPPORT} Component={TeamsSupportPage} />
    <Route path={PATH.SLACK_SUPPORT} Component={SlackSupportPage} />
    <Route path={PATH.INTEGRATIONS} Component={IntegrationsPage} />
    <Route
      path={PATH.HUBSPOT_INTEGRATION}
      element={<CrmIntegrationPage provider="hubspot" />}
    />
    <Route
      path={PATH.SALESFORCE_INTEGRATION}
      element={<CrmIntegrationPage provider="salesforce" />}
    />

    {/* Error Page Routes */}
    <Route path={PATH.ACCESS_DENIED} Component={AccessDeniedPage} />

    {/*
          Main app routes share a persistent MainLayout shell.
          The layout stays mounted across tab navigations so the header
          and nav highlight update instantly while page content loads.
        */}
    <Route element={<MainLayout />}>
      <Route path="/jobs/:kind/:jobId" element={<PrivateRoute roles={ALL_ROLES} component={JobResultPage} />} />
      <Route
        path={PATH.ASK_INTENTICO}
        element={
          <PrivateRoute
            roles={ALL_ROLES}
            component={AskIntenticoPage}
            requiredCapability={CAPABILITY_KEYS.ACCOUNTS}
          />
        }
      />
      <Route
        path="/dismissal-decisions/:decisionId"
        element={
          <PrivateRoute roles={ALL_ROLES} component={DismissalDecisionPage} />
        }
      />
      <Route
        path={PATH.Sales_Home}
        element={
          <PrivateRoute
            roles={ALL_ROLES}
            component={SalesHomePage}
            requiredCapability={CAPABILITY_KEYS.HOTLIST}
          />
        }
      />
      <Route
        path={PATH.ALL_ACCOUNTS}
        element={
          <PrivateRoute
            roles={ALL_ROLES}
            component={UpdateAccountPage}
            requiredCapability={CAPABILITY_KEYS.ACCOUNTS}
          />
        }
      />
      <Route
        path={PATH.ACCOUNT_DETAILS}
        element={
          <PrivateRoute
            roles={ALL_ROLES}
            component={AccountRouteWrapper}
            requiredCapability={CAPABILITY_KEYS.ACCOUNTS}
          />
        }
      />
      <Route
        path={`${PATH.INDUSTRY_INSIGHTS}/*`}
        element={
          <PrivateRoute
            roles={ALL_ROLES}
            component={IndustryInsightsPage}
            requiredCapability={CAPABILITY_KEYS.INDUSTRIES}
          />
        }
      />
      <Route
        path={PATH.SETTINGS}
        element={<PrivateRoute roles={ALL_ROLES} component={SettingsPage} />}
      />
      <Route
        path={PATH.CRM_MATCH_REVIEWS}
        element={
          <PrivateRoute
            roles={ALL_ROLES}
            component={CrmMatchReviewsPage}
            requiredCapability={CAPABILITY_KEYS.CRM_SYNC}
          />
        }
      />
      <Route
        path={PATH.MY_ASSETS}
        element={
          <PrivateRoute
            roles={ALL_ROLES}
            component={MyAssetsPage}
            requiredCapability={CAPABILITY_KEYS.ASSETS}
          />
        }
      />
      <Route
        path={PATH.ACTIVITY}
        element={<PrivateRoute roles={ALL_ROLES} component={ActivityPage} />}
      />
      <Route
        path={PATH.ADMIN_CREATE_CLIENT}
        element={
          <PrivateRoute roles={ADMIN_ONLY_ROLES} component={CreateClientPage} />
        }
      />
      <Route
        path={PATH.CREATE_CAMPAIGN}
        element={
          <PrivateRoute
            roles={CAMPAIGN_ADMIN_ROLES}
            component={CreateCampaignPage}
          />
        }
      />
      <Route
        path={PATH.ADMIN_CLIENT_READINESS}
        element={
          <PrivateRoute
            roles={ADMIN_ONLY_ROLES}
            component={ClientReadinessPage}
          />
        }
      />
      <Route
        path={PATH.ADMIN_COSTS}
        element={
          <PrivateRoute roles={ADMIN_ONLY_ROLES} component={CostsPage} />
        }
      />
      <Route
        path={PATH.ADMIN_CLIENT_LIMITS}
        element={
          <PrivateRoute roles={ADMIN_ONLY_ROLES} component={ClientLimitsPage} />
        }
      />
    </Route>

    {/* Legal Routes */}
    <Route
      path={PATH.ADDENDUMS}
      element={<LegalTemplatePage documentType="addendums" />}
    />
    <Route
      path={PATH.GUIDELINES}
      element={<LegalTemplatePage documentType="guidelines" />}
    />
    <Route
      path={PATH.ACCEPTABLE_USE_POLICY}
      element={<LegalTemplatePage documentType="acceptable-use" />}
    />
    <Route
      path={PATH.PRIVACY_POLICY}
      element={<LegalTemplatePage documentType="privacy" />}
    />
    <Route
      path={PATH.TERMS_OF_SERVICE}
      element={<LegalTemplatePage documentType="terms-of-service" />}
    />

    {/* Default Not Found Page for Undefined Routes */}
    <Route path="*" Component={NotFoundPage} />
  </Route>,
);
const appRouter = createBrowserRouter(appRoutes);
